import type { RevertIssue, RevertPreview, RevertResult, RevertTarget } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { AlertTriangle, Ban, Info } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';
import { ApiError, api } from '../../../lib/api';
import { dateTime, plural } from '../../../lib/format';
import { Button } from '../../../ui/button';
import { Dialog } from '../../../ui/dialog';
import { ErrorBox, Spinner, toast } from '../../../ui/feedback';
import { Checkbox, Field, Textarea } from '../../../ui/form';
import { actorName, ChangeRows, EFFECT_WORD } from './shared';

/** Что отменяется — подпись кнопки */
const APPLY_WORD: Record<RevertTarget['kind'], string> = {
  request: 'Отменить изменения',
  record: 'Вернуть запись',
  restore: 'Восстановить',
  undo: 'Отменить отмену',
};

function IssueList({
  items,
  tone,
  title,
}: {
  items: RevertIssue[];
  tone: 'danger' | 'warning' | 'info';
  title: string;
}) {
  if (!items.length) return null;
  const Icon = tone === 'danger' ? Ban : tone === 'warning' ? AlertTriangle : Info;
  return (
    <section
      className={clsx(
        'rounded-md px-3 py-2.5 text-[13px]',
        tone === 'danger' && 'bg-danger-soft text-danger',
        tone === 'warning' && 'bg-warning-soft text-warning',
        tone === 'info' && 'bg-info-soft text-info',
      )}
    >
      <h3 className="flex items-center gap-1.5 font-semibold">
        <Icon className="size-4 shrink-0" aria-hidden />
        {title}
      </h3>
      <ul className="mt-1.5 flex list-disc flex-col gap-1 pl-5">
        {items.map((i, n) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: пункты без своего номера, порядок не меняется
          <li key={n} className="[overflow-wrap:anywhere]">
            {i.tableTitle || i.recordLabel ? (
              <b className="font-semibold">{[i.tableTitle, i.recordLabel].filter(Boolean).join(' ')}: </b>
            ) : null}
            {i.text}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-[14px] font-semibold text-ink">{title}</h3>
      {children}
    </section>
  );
}

/**
 * Отмена из журнала: сначала пробный прогон в базе (что изменится, что мешает, что подтвердить), затем отмена
 * с причиной ровно по показанному плану. Если журнал изменился, пока человек смотрел, — прогон повторяется.
 */
export function RevertDialog({
  target,
  title,
  description,
  onClose,
}: {
  target: RevertTarget;
  title: string;
  description?: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const reasonId = useId();
  const [withDependents, setWithDependents] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const preview = useQuery({
    queryKey: ['journal', 'preview', target, withDependents],
    queryFn: () => api<RevertPreview>('/admin/journal/revert/preview', { body: { target, withDependents } }),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
  const p = preview.data;
  const apply = useMutation({
    mutationFn: () =>
      api<RevertResult>('/admin/journal/revert', {
        body: { target, withDependents, reason: reason.trim(), confirm: confirmed, planHash: p!.planHash },
      }),
    onSuccess: (r) => {
      const n = r.counts.restored + r.counts.updated + r.counts.deleted;
      toast(
        n
          ? `Готово: ${plural(n, 'запись возвращена', 'записи возвращены', 'записей возвращено')} к прежнему состоянию`
          : 'Готово: записи уже были в прежнем состоянии',
      );
      void qc.invalidateQueries({ queryKey: ['journal'] });
      void qc.invalidateQueries({ queryKey: ['history'] });
      onClose();
    },
    onError: (err) => {
      // Журнал изменился после предпросмотра — показать новый план, причину не стирать
      if (err instanceof ApiError && err.code === 'plan_changed') {
        setConfirmed(false);
        void preview.refetch();
      }
    },
  });
  const reasonOk = reason.trim().length >= 5;
  const canApply = Boolean(p?.ok) && reasonOk && (!p?.needsConfirm || confirmed) && !preview.isFetching;
  const total = p ? p.counts.restored + p.counts.updated + p.counts.deleted : 0;

  return (
    <Dialog
      open
      onOpenChange={(o) => (o ? null : onClose())}
      title={title}
      description={description}
      width="lg"
      footer={
        <>
          <Button onClick={onClose}>Закрыть</Button>
          <Button
            variant="danger"
            onClick={() => apply.mutate()}
            loading={apply.isPending}
            disabled={!canApply}
          >
            {APPLY_WORD[target.kind]}
          </Button>
        </>
      }
    >
      {preview.isPending ? (
        <Spinner label="Пробный прогон в базе" />
      ) : preview.isError ? (
        <ErrorBox error={preview.error} />
      ) : p ? (
        <div className="flex flex-col gap-4">
          <p className="text-[14px] text-ink">
            {total ? (
              <>
                Пробный прогон: {plural(total, 'запись', 'записи', 'записей')} —{' '}
                {[
                  p.counts.restored ? `восстановится ${p.counts.restored}` : null,
                  p.counts.updated ? `изменится ${p.counts.updated}` : null,
                  p.counts.deleted ? `удалится ${p.counts.deleted}` : null,
                ]
                  .filter(Boolean)
                  .join(', ')}
                . Отменяется всё действие целиком — так связанные записи и итоги остаются согласованными.
              </>
            ) : (
              'Пробный прогон ничего не изменил бы.'
            )}
          </p>

          {p.requests.length ? (
            <Block title={p.requests.length > 1 ? 'Отменяемые действия' : 'Отменяемое действие'}>
              <ul className="flex flex-col divide-y divide-line rounded-md border border-line">
                {p.requests.map((r) => (
                  <li key={r.key} className="flex flex-col gap-0.5 px-3 py-2 text-[13px]">
                    <span className="font-medium [overflow-wrap:anywhere] text-ink">{r.title}</span>
                    <span className="text-ink-2">
                      <span className="num">{dateTime(r.at)}</span> · {actorName(r.actor)}
                      {r.dependent ? ' · позднее изменение тех же записей' : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </Block>
          ) : null}

          {p.dependents.length && !withDependents ? (
            <section className="flex flex-col gap-2 rounded-md bg-warning-soft px-3 py-2.5 text-[13px] text-warning">
              <h3 className="flex items-center gap-1.5 font-semibold">
                <AlertTriangle className="size-4 shrink-0" aria-hidden />
                Эти записи потом меняли ещё {plural(p.dependents.length, 'раз', 'раза', 'раз')}
              </h3>
              <p>Отменить можно только вместе с более поздними изменениями, иначе они потеряют смысл:</p>
              <ul className="flex list-disc flex-col gap-0.5 pl-5">
                {p.dependents.map((d) => (
                  <li key={d.key} className="[overflow-wrap:anywhere]">
                    {d.title} — <span className="num">{dateTime(d.at)}</span>, {actorName(d.actor)}
                  </li>
                ))}
              </ul>
              <Checkbox
                label="Отменить вместе с ними"
                checked={withDependents}
                onChange={(e) => {
                  setConfirmed(false);
                  setWithDependents(e.target.checked);
                }}
              />
            </section>
          ) : null}

          <IssueList items={p.blocks} tone="danger" title="Отменить нельзя" />
          {p.confirms.length ? (
            <div className="flex flex-col gap-2">
              <IssueList items={p.confirms} tone="warning" title="Проверьте перед отменой" />
              <Checkbox
                label="Понимаю, отменить вместе с этим"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
            </div>
          ) : null}
          <IssueList items={p.notes} tone="info" title="Для сведения" />

          {p.effects.length ? (
            <Block title="Что изменится">
              <ol className="flex flex-col gap-3">
                {p.effects.map((e, n) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: одна запись может меняться дважды — номер по порядку
                  <li key={n} className="text-[13px]">
                    <div className="text-ink">
                      <b className="font-semibold">{EFFECT_WORD[e.op]}</b> {e.tableTitle.toLowerCase()}
                      {e.recordLabel ? ` ${e.recordLabel}` : ''}
                    </div>
                    {e.op === 'U' ? (
                      <ChangeRows op="U" table={e.table} changes={e.changes} limit={4} />
                    ) : null}
                  </li>
                ))}
              </ol>
              {p.effectsTruncated ? (
                <p className="text-[12px] text-ink-2">Показана часть записей — итог выше считает все.</p>
              ) : null}
            </Block>
          ) : null}

          {p.ok ? (
            <Field
              label="Причина"
              htmlFor={reasonId}
              hint="Видна в журнале: кто, когда и почему отменил"
              error={reason && !reasonOk ? 'Опишите причину — хотя бы несколько слов' : undefined}
            >
              <Textarea
                id={reasonId}
                value={reason}
                maxLength={500}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Например: ошибочно удалили договор при чистке черновиков"
              />
            </Field>
          ) : null}
          {apply.error ? <ErrorBox error={apply.error} /> : null}
        </div>
      ) : null}
    </Dialog>
  );
}
